import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AppLayout from "../../../../shared/components/AppLayout";
import Button from "../../../../shared/components/Button";
import EmptyState from "../../../../shared/components/EmptyState";
import IconButtonContent from "../../../../shared/components/IconButtonContent";
import LoadingPage from "../../../../shared/components/LoadingPage";
import SearchInput from "../../../../shared/components/SearchInput";
import Spinner from "../../../../shared/components/Spinner";
import Toast from "../../../../shared/components/Toast";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import {
  requestCurrentUserLocation,
  useUserLocation,
} from "../../../../shared/location/userLocation";
import type { UserLocation } from "../../../../shared/location/userLocation";
import ClubActionButtons from "../components/ClubActionButtons";
import ClubRequestModal from "../components/ClubRequestModal";
import {
  listClubs,
  requestClubRegistration,
} from "../../shared/services/clubsService";
import type { Club, ClubRequestPayload } from "../../shared/types/club";
import {
  canResolveClubMapPosition,
  hasClubCoordinates,
} from "../../shared/utils/clubMapPosition";
import "./ClubsPage.css";

const LeafletClubsMap = lazy(() => import("../components/LeafletClubsMap"));

const BRAZIL_STATES = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

type LocationMessageTone = "success" | "warning" | "error";

type LocationMessage = {
  text: string;
  tone: LocationMessageTone;
};

function haversineKm(a: UserLocation, b: UserLocation) {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const earthKm = 6371;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * earthKm * Math.asin(Math.sqrt(h));
}

function applyDistanceOrdering(clubs: Club[], location: UserLocation | null) {
  if (!location) {
    return clubs.map((club) => ({ ...club, distanceKm: null }));
  }

  return clubs.map((club) => {
    if (!hasClubCoordinates(club)) {
      return { ...club, distanceKm: null };
    }

    return {
      ...club,
      distanceKm: haversineKm(location, {
        latitude: Number(club.latitude),
        longitude: Number(club.longitude),
      }),
    };
  });
}

function sortClubs(clubs: Club[], prioritizeDistance: boolean) {
  return [...clubs].sort((left, right) => {
    if (prioritizeDistance) {
      const leftDistance = Number.isFinite(left.distanceKm)
        ? Number(left.distanceKm)
        : Number.POSITIVE_INFINITY;
      const rightDistance = Number.isFinite(right.distanceKm)
        ? Number(right.distanceKm)
        : Number.POSITIVE_INFINITY;
      if (leftDistance !== rightDistance) return leftDistance - rightDistance;
    }

    return left.name.localeCompare(right.name, "pt-BR", {
      sensitivity: "base",
    });
  });
}

function PublicClubsMap({
  clubs,
  userLocation,
  refreshKey,
}: {
  clubs: Club[];
  userLocation: UserLocation | null;
  refreshKey: number;
}) {
  const t = siteTexts.clubs;
  const clubsWithMapPosition = useMemo(
    () => clubs.filter(canResolveClubMapPosition),
    [clubs],
  );

  if (clubsWithMapPosition.length === 0) {
    return (
      <div className="clubs-page__map-surface clubs-page__map-surface--empty">
        <p>{t.noMapCoordinates}</p>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="clubs-page__map-surface clubs-page__map-loading">
          <span className="material-symbols-outlined" aria-hidden="true">
            map
          </span>
          <strong>{siteTexts.common.loading}</strong>
        </div>
      }
    >
      <LeafletClubsMap
        key={refreshKey}
        clubs={clubsWithMapPosition}
        userLocation={userLocation}
      />
    </Suspense>
  );
}

export default function ClubsPage() {
  const t = siteTexts.clubs;
  const [clubs, setClubs] = useState<Club[]>([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [searchDraft, setSearchDraft] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [cityDraft, setCityDraft] = useState("");
  const [cityValue, setCityValue] = useState("");
  const [state, setState] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "map">("list");
  const [error, setError] = useState("");
  const [locationMessage, setLocationMessage] =
    useState<LocationMessage | null>(null);
  const userLocation = useUserLocation();
  const [reloadKey, setReloadKey] = useState(0);
  const [mapRefreshKey, setMapRefreshKey] = useState(0);
  const [mapViewReady, setMapViewReady] = useState(false);
  const [clubRequestOpen, setClubRequestOpen] = useState(false);
  const [clubRequestSaving, setClubRequestSaving] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    variant: "success" | "error";
  } | null>(null);
  const firstLoadRef = useRef(true);

  const loadClubs = useCallback(
    async ({
      search,
      city,
      stateCode,
      location,
    }: {
      search: string;
      city: string;
      stateCode: string;
      location: UserLocation | null;
    }) => {
      const response = await listClubs({
        search,
        city,
        state: stateCode,
      });
      return sortClubs(
        applyDistanceOrdering(response.clubs || [], location),
        Boolean(location),
      );
    },
    [],
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearchValue(searchDraft);
      setCityValue(cityDraft);
    }, 1000);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [cityDraft, searchDraft]);

  useEffect(() => {
    let active = true;
    const isInitialRequest = firstLoadRef.current;
    if (isInitialRequest) {
      setListLoading(false);
    } else {
      setListLoading(true);
    }
    setError("");
    loadClubs({
      search: searchValue,
      city: cityValue,
      stateCode: state,
      location: userLocation,
    })
      .then((nextClubs) => {
        if (!active) return;
        setClubs(nextClubs);
      })
      .catch(() => {
        if (!active) return;
        setClubs([]);
        setError(t.loadError);
      })
      .finally(() => {
        if (!active) return;
        if (isInitialRequest) {
          firstLoadRef.current = false;
          setHasLoadedOnce(true);
        }
        setListLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    cityValue,
    loadClubs,
    reloadKey,
    searchValue,
    state,
    t.loadError,
    userLocation,
  ]);

  useEffect(() => {
    if (viewMode !== "map" || !mapViewReady) return;
    setMapRefreshKey((current) => current + 1);
  }, [clubs, mapViewReady, userLocation, viewMode]);

  useEffect(() => {
    if (viewMode !== "map") {
      setMapViewReady(false);
      return;
    }

    let frameId = 0;
    let nestedFrameId = 0;
    let timeoutId = 0;

    setMapViewReady(false);

    frameId = window.requestAnimationFrame(() => {
      nestedFrameId = window.requestAnimationFrame(() => {
        timeoutId = window.setTimeout(() => {
          setMapViewReady(true);
        }, 120);
      });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.cancelAnimationFrame(nestedFrameId);
      window.clearTimeout(timeoutId);
    };
  }, [viewMode]);

  const useCurrentLocation = async () => {
    setLocationMessage(null);
    setSearchValue(searchDraft);
    setCityValue(cityDraft);
    setListLoading(true);
    try {
      await requestCurrentUserLocation({ force: true });
      setViewMode("map");
      setLocationMessage({ text: t.locationApplied, tone: "success" });
    } catch (_err) {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        setLocationMessage({ text: t.locationUnavailable, tone: "error" });
        return;
      }
      setLocationMessage({ text: t.locationDenied, tone: "warning" });
    } finally {
      setListLoading(false);
    }
  };

  const submitClubRequest = async (payload: ClubRequestPayload) => {
    setClubRequestSaving(true);
    try {
      await requestClubRegistration(payload);
      setClubRequestOpen(false);
      setToast({
        message: t.requestClub.success,
        variant: "success",
      });
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : t.requestClub.error,
        variant: "error",
      });
      throw err;
    } finally {
      setClubRequestSaving(false);
    }
  };

  return (
    <AppLayout
      title={<span className="clubs-page-title">{t.title}</span>}
      subtitle={t.subtitle}
      headerRight={
        <Button
          type="button"
          variant="primary"
          onClick={() => setClubRequestOpen(true)}
        >
          <IconButtonContent icon="add_location_alt">
            {t.requestClub.action}
          </IconButtonContent>
        </Button>
      }
    >
      <main className="clubs-page">
        <section className="clubs-page__filters" aria-label={t.filtersLabel}>
          <SearchInput
            inputType="search"
            value={searchDraft}
            placeholder={t.searchPlaceholder}
            aria-label={t.searchPlaceholder}
            onChange={(event) => setSearchDraft(event.target.value)}
            onClear={() => setSearchDraft("")}
          />
          <label className="clubs-page__field">
            <span className="clubs-page__field-label">
              <span className="material-symbols-outlined" aria-hidden="true">
                location_city
              </span>
              {t.cityFilter}
            </span>
            <input
              value={cityDraft}
              placeholder={t.cityPlaceholder}
              onChange={(event) => setCityDraft(event.target.value)}
            />
          </label>
          <label className="clubs-page__field">
            <span className="clubs-page__field-label">
              <span className="material-symbols-outlined" aria-hidden="true">
                map
              </span>
              {t.stateFilter}
            </span>
            <select
              value={state}
              onChange={(event) => setState(event.target.value)}
            >
              <option value="">{t.allStates}</option>
              {BRAZIL_STATES.map((stateOption) => (
                <option key={stateOption} value={stateOption}>
                  {stateOption}
                </option>
              ))}
            </select>
          </label>
          <div className="clubs-page__view-actions">
            <Button
              type="button"
              variant={viewMode === "list" ? "primary" : "pill"}
              onClick={() => setViewMode("list")}
            >
              <IconButtonContent icon="view_list">
                {t.listView}
              </IconButtonContent>
            </Button>
            <Button
              type="button"
              variant={viewMode === "map" ? "primary" : "pill"}
              onClick={() => setViewMode("map")}
            >
              <IconButtonContent icon="map">{t.mapView}</IconButtonContent>
            </Button>
            <Button type="button" variant="pill" onClick={useCurrentLocation}>
              <IconButtonContent icon="my_location">
                {t.useLocation}
              </IconButtonContent>
            </Button>
          </div>
        </section>

        {locationMessage && (
          <div
            className={`clubs-page__location-message clubs-page__location-message--${locationMessage.tone}`}
            role={locationMessage.tone === "success" ? "status" : "alert"}
            aria-live="polite"
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              {locationMessage.tone === "success"
                ? "near_me"
                : locationMessage.tone === "warning"
                  ? "location_off"
                  : "error"}
            </span>
            <strong>{locationMessage.text}</strong>
          </div>
        )}

        {!hasLoadedOnce ? (
          <LoadingPage label={siteTexts.common.loading} />
        ) : error ? (
          <section className="clubs-page__error" role="alert">
            <span className="material-symbols-outlined" aria-hidden="true">
              error
            </span>
            <div>
              <strong>{error}</strong>
              <Button
                type="button"
                variant="danger"
                onClick={() => setReloadKey((current) => current + 1)}
              >
                {siteTexts.common.serverUnavailable.actions.retry}
              </Button>
            </div>
          </section>
        ) : (
          <section className="clubs-page__results" aria-busy={listLoading}>
            {listLoading ? (
              <div
                className="clubs-page__loading-bar"
                role="status"
                aria-live="polite"
              >
                <Spinner size="sm" />
                <span>{siteTexts.common.loading}</span>
              </div>
            ) : null}

            {clubs.length === 0 ? (
              listLoading ? (
                <div className="clubs-page__loading-empty" />
              ) : (
                <EmptyState
                  className="clubs-page__empty"
                  icon={
                    <span className="material-symbols-outlined">
                      sports_tennis
                    </span>
                  }
                  title={t.empty}
                  action={
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => setClubRequestOpen(true)}
                    >
                      <IconButtonContent icon="add_location_alt">
                        {t.requestClub.action}
                      </IconButtonContent>
                    </Button>
                  }
                />
              )
            ) : viewMode === "map" ? (
              <section className="clubs-page__map-panel" aria-label={t.mapView}>
                {mapViewReady ? (
                  <PublicClubsMap
                    clubs={clubs}
                    userLocation={userLocation}
                    refreshKey={mapRefreshKey}
                  />
                ) : (
                  <div className="clubs-page__map-surface clubs-page__map-loading">
                    <span
                      className="material-symbols-outlined"
                      aria-hidden="true"
                    >
                      map
                    </span>
                    <strong>{siteTexts.common.loading}</strong>
                  </div>
                )}
              </section>
            ) : (
              <section className="clubs-page__grid" aria-label={t.title}>
                {clubs.map((club) => (
                  <article className="clubs-page__card" key={club.id}>
                    <a
                      className="clubs-page__cover"
                      href={`#/clubes/${club.slug}`}
                    >
                      <img src={club.coverImageUrl} alt="" loading="lazy" />
                      <span
                        className="clubs-page__cover-shade"
                        aria-hidden="true"
                      />
                      <span className="clubs-page__logo-frame">
                        <img
                          className="clubs-page__logo"
                          src={club.logoUrl}
                          alt={club.name}
                          loading="lazy"
                        />
                      </span>
                    </a>
                    <div className="clubs-page__card-body">
                      <div className="clubs-page__card-copy">
                        <h2>
                          <a href={`#/clubes/${club.slug}`}>{club.name}</a>
                        </h2>
                        <div className="clubs-page__meta-row">
                          <span className="clubs-page__meta-chip">
                            <span
                              className="material-symbols-outlined"
                              aria-hidden="true"
                            >
                              location_on
                            </span>
                            {club.city} / {club.state}
                          </span>
                          {club.distanceKm !== null &&
                          club.distanceKm !== undefined ? (
                            <span className="clubs-page__meta-chip">
                              <span
                                className="material-symbols-outlined"
                                aria-hidden="true"
                              >
                                near_me
                              </span>
                              {club.distanceKm.toFixed(1)} km
                            </span>
                          ) : null}
                        </div>
                        <p>
                          {[club.address, club.number, club.neighborhood]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      </div>
                    </div>
                    <ClubActionButtons club={club} compact includeDetail />
                  </article>
                ))}
              </section>
            )}
          </section>
        )}
        <ClubRequestModal
          open={clubRequestOpen}
          saving={clubRequestSaving}
          onSubmit={submitClubRequest}
          onClose={() => {
            if (!clubRequestSaving) setClubRequestOpen(false);
          }}
        />
        {toast && (
          <Toast
            message={toast.message}
            variant={toast.variant}
            onClose={() => setToast(null)}
          />
        )}
      </main>
    </AppLayout>
  );
}
